// File: src/pages/master/UserListMasterCompact.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Row,
  Col,
  Card,
  Button,
  Input,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
} from "reactstrap";
import { Link } from "react-router-dom";
import {
  FaPlus,
  FaFilter,
  FaArrowAltCircleLeft,
  FaArrowAltCircleRight,
} from "react-icons/fa";
import { ImDownload } from "react-icons/im";
import { IoMdRemoveCircleOutline } from "react-icons/io";
import {
  BsFillArrowDownSquareFill,
  BsFillArrowUpSquareFill,
} from "react-icons/bs";
import swal from "sweetalert";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";

// imports from your import layer (kept per your instruction)
import { _Get, _GetList } from "../../import/ApiEndPoint/ApiCalling";
import {
  Create_Account_List,
  Super_Admin_List,
} from "../../import/ApiEndPoint/Api";
import SuperAdminUI from "../../import/SuperAdminUI";
import { CheckPermission } from "../../import/CheckPermission";

//
// Inline CSS injector (run once) — includes fixes for no-wrap toolbar and modal look
//
const injectOnce = () => {
  if (document.getElementById("userlist-compact-css")) return;
  const s = document.createElement("style");
  s.id = "userlist-compact-css";
  s.textContent = `
    .u-toolbar .count-pill{ background:#eef2ff; color:#3730a3; font-weight:700; padding:6px 10px; border-radius:999px; }
    .u-card{ border-radius:14px; border:1px solid #e5e7eb; }
    .toolbar-actions { display:flex; gap:8px; align-items:center; } /* NO wrap */
    .toolbar-actions .btn { white-space:nowrap; display:flex; align-items:center; gap:8px; padding:6px 12px; height:32px; font-weight:600; font-size:13px; }
    .btn-icon-only { width:36px; min-width:36px; padding:0; justify-content:center; display:inline-flex; align-items:center; }
    .btn-outline-small { height:32px; padding:6px 10px; font-weight:600; }
    .u-table-wrap{ width:100%; overflow:auto; border:1px solid #e5e7eb; border-radius:14px; background:#fff; }
    .u-table{ width:100%; border-collapse:collapse; table-layout:auto; min-width:1100px; }
    .u-table thead th{ background:#f4f7f6; color:#0f172a; font-weight:700; text-transform:uppercase; font-size:12px; letter-spacing:.03em; border-bottom:1px solid #e5e7eb; padding:6px 8px; text-align:center; white-space:nowrap; position: sticky; top: 0; z-index:1; }
    .u-table tbody td{ padding:6px 8px; border-bottom:1px solid #f1f5f9; vertical-align:middle; color:#0f172a; font-size:13px; text-align:center; }
    .u-table tbody tr:hover{ background:#f9fbff; }
    .actions{ display:flex; justify-content:center; align-items:center; gap:8px; }
    .chip{ display:inline-grid; place-items:center; width:28px; height:28px; border-radius:16px; cursor:pointer; }
    .chip-edit{ background:#d46f10; }
    .chip-view{ background:#39cccc; }
    .chip-del{ background:#ec1809; }
    .chip-link{ color:#0f172a; text-decoration:none; }
    .dup-note{ background:#e6f2ff; padding:0 6px; border:1px solid #cfe7ff; border-radius:4px; }

    /* Column chooser list boxes + look like original */
    .listbox { border: 1px solid #e5e7eb; border-radius: 10px; max-height: 420px; overflow: auto; background: #fff; padding: 8px; }
    .pick-item { padding: 8px 10px; border-radius: 8px; border: 1px solid #e5e7eb; margin-bottom: 6px; background: #fff; cursor:pointer; }
    .pick-item.active { background: #1877f2; color: #fff; }
    .col-arrow-area { display:flex; flex-direction:column; justify-content:center; align-items:center; gap:12px; }
    .col-arrow-area .arrow-btn { cursor:pointer; }
    .modalbodyhead { padding: 16px 20px; }
  `;
  document.head.appendChild(s);
};

const toStr = (v) => (v === null || v === undefined ? "" : String(v));
const toStrTrim = (v) =>
  v === null || v === undefined ? "" : String(v).trim();

// Base columns definition (matches keys you used)
const BASE_COLS = [
  { field: "id", headerName: "id" },
  { field: "firstName", headerName: "Full Name" },
  { field: "email", headerName: "Email" },
  { field: "mobileNumber", headerName: "Mobile Number" },
  { field: "pincode", headerName: "Pin Code" },
  { field: "Aadhar_No", headerName: "Aadhar No" },
  { field: "Account_Name", headerName: "Bank Name" },
  { field: "Account_No", headerName: "Account No" },
  { field: "Ifsc_code", headerName: "IFSC Code" },
  { field: "City", headerName: "City" },
  { field: "State", headerName: "State" },
  { field: "DL_Num", headerName: "DL No." },
  { field: "DOB", headerName: "Date of Birth" },
  { field: "Father_name", headerName: "Father Name" },
  { field: "Pan_No", headerName: "Pan No" },
  { field: "PassportNo", headerName: "Passport No" },
  { field: "last_job_Salary", headerName: "Salary" },
  { field: "last_job_Profile", headerName: "Last Job Profile" },
  { field: "last_job_address", headerName: "Last Job Address" },
  { field: "last_job_firm_name", headerName: "Firm Name" },
  { field: "address1", headerName: "Address" },
  { field: "address2", headerName: "Address One" },
  { field: "rolename.roleName", headerName: "Role Name" },
];

const STORAGE_COLS_KEY = "AccountSearch";
const STORAGE_SA_KEY = "SuperadminIdByMaster";

export default function UserListMasterCompact() {
  injectOnce();

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [isMaster, setIsMaster] = useState(false);

  // duplicates across SA
  const [dupPan, setDupPan] = useState(new Set());
  const [dupAadhar, setDupAadhar] = useState(new Set());

  // Column chooser modal state
  const [modal, setModal] = useState(false);
  const [allColumnDefs, setAllColumnDefs] = useState(() => BASE_COLS.slice());
  const [selectedColumnDefs, setSelectedColumnDefs] = useState(() => {
    // load saved AccountSearch (support either array of field strings or AG Grid objects)
    try {
      const raw = localStorage.getItem(STORAGE_COLS_KEY);
      if (!raw) return BASE_COLS.slice();
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length) {
        // if array of objects with headerName/field -> keep, else if array of strings -> map to objects
        if (typeof parsed[0] === "string") {
          return parsed
            .map((k) => {
              const found = BASE_COLS.find((b) => b.field === k);
              return found
                ? { headerName: found.headerName, field: found.field }
                : null;
            })
            .filter(Boolean);
        } else if (parsed[0] && (parsed[0].field || parsed[0].headerName)) {
          // keep only matching known fields to avoid garbage
          return parsed
            .map((p) => {
              const f = p.field || p.headerName;
              const found = BASE_COLS.find(
                (b) => b.field === f || b.headerName === f
              );
              return found
                ? {
                    headerName: p.headerName || found.headerName,
                    field: p.field || found.field,
                  }
                : null;
            })
            .filter(Boolean);
        }
      }
    } catch (e) {
      // ignore
    }
    // fallback - default to all base cols as objects
    return BASE_COLS.map((c) => ({ headerName: c.headerName, field: c.field }));
  });

  // available selections for "Available Columns" checkboxes (set of fields)
  const availableSelectedRef = useRef(new Set());
  // visible selection index for reordering / remove
  const [visibleIndex, setVisibleIndex] = useState(-1);

  useEffect(() => {
    const ud = JSON.parse(localStorage.getItem("userData") || "{}");
    setIsMaster(ud?.rolename?.roleName === "MASTER");
    bootstrapLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // bootstrap: build duplicates and load initial users (either SA selected or own org)
  const bootstrapLoad = async () => {
    setLoading(true);
    try {
      await buildGlobalDuplicates();
      const prev = localStorage.getItem(STORAGE_SA_KEY) || "";
      const [sid, sdb] = String(prev).split(" ");
      if (sid && sdb) await fetchUsersFor(sid, sdb);
      else {
        const me = JSON.parse(localStorage.getItem("userData") || "{}");
        await fetchUsersFor(me?._id, me?.database);
      }
    } catch (err) {
      console.error(err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const buildGlobalDuplicates = async () => {
    try {
      const res = await _GetList(Super_Admin_List);
      const superAdmins = Array.isArray(res?.SuperAdmin) ? res.SuperAdmin : [];
      const all = await Promise.all(
        superAdmins.map(async (sa) => {
          const url = `${Create_Account_List + sa?._id}/`;
          try {
            const data = await _Get(url, sa?.database);
            const list = Array.isArray(data?.adminDetails)
              ? data.adminDetails
              : [];
            return list.map((u) => ({
              saId: sa?._id,
              pan: toStrTrim(u?.Pan_No),
              aadhar: toStrTrim(u?.Aadhar_No),
            }));
          } catch {
            return [];
          }
        })
      );
      const flattened = all.flat();
      const panToSA = new Map();
      const aadharToSA = new Map();
      flattened.forEach(({ saId, pan, aadhar }) => {
        if (pan) {
          if (!panToSA.has(pan)) panToSA.set(pan, new Set());
          panToSA.get(pan).add(saId);
        }
        if (aadhar) {
          if (!aadharToSA.has(aadhar)) aadharToSA.set(aadhar, new Set());
          aadharToSA.get(aadhar).add(saId);
        }
      });
      const dupPanSet = new Set(
        [...panToSA.entries()].filter(([, set]) => set.size > 1).map(([k]) => k)
      );
      const dupAadSet = new Set(
        [...aadharToSA.entries()]
          .filter(([, set]) => set.size > 1)
          .map(([k]) => k)
      );
      setDupPan(dupPanSet);
      setDupAadhar(dupAadSet);
    } catch (err) {
      console.error("Failed to build duplicates:", err);
      setDupPan(new Set());
      setDupAadhar(new Set());
    }
  };

  const fetchUsersFor = async (ownerId, db) => {
    if (!ownerId || !db) return;
    try {
      setLoading(true);
      const url = `${Create_Account_List + ownerId}/`;
      const res = await _Get(url, db);
      const me = JSON.parse(localStorage.getItem("userData") || "{}");
      const list = Array.isArray(res?.adminDetails) ? res.adminDetails : [];
      const filtered = list.filter(
        (u) =>
          u?.status === "Active" &&
          u?.rolename?.roleName !== me?.rolename?.roleName
      );
      setRows(filtered);
    } catch (err) {
      console.error("fetchUsersFor error:", err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  // SuperAdmin dropdown handlers
  const handleDropdownChange = (selectedValue) => {
    localStorage.setItem(STORAGE_SA_KEY, JSON.stringify(selectedValue));
  };
  const handleParentSubmit = async (e) => {
    e?.preventDefault?.();
    const raw = JSON.parse(localStorage.getItem(STORAGE_SA_KEY) || '""');
    const [id, db] = String(raw).split(" ");
    if (!id || !db) {
      const me = JSON.parse(localStorage.getItem("userData") || "{}");
      await fetchUsersFor(me?._id, me?.database);
      return;
    }
    await fetchUsersFor(id, db);
  };

  // Columns list used in table header (order from selectedColumnDefs)
  const visibleColumns = useMemo(
    () =>
      selectedColumnDefs.map((c) => ({
        field: c.field,
        headerName: c.headerName,
      })),
    [selectedColumnDefs]
  );

  // search across many fields
  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((r) => {
      const fields = [
        r?.id,
        r?.firstName,
        r?.lastName,
        r?.email,
        r?.mobileNumber,
        r?.pincode,
        r?.Aadhar_No,
        r?.Pan_No,
        r?.City,
        r?.State,
        r?.DOB,
        r?.last_job_Salary,
        r?.last_job_Profile,
        r?.last_job_address,
        r?.last_job_firm_name,
        r?.address1,
        r?.address2,
        r?.rolename?.roleName,
      ];
      return fields.some((f) => toStr(f).toLowerCase().includes(term));
    });
  }, [rows, search]);

  const isDuplicateRow = (u) => {
    const pan = toStrTrim(u?.Pan_No);
    const aadhar = toStrTrim(u?.Aadhar_No);
    return (pan && dupPan.has(pan)) || (aadhar && dupAadhar.has(aadhar));
  };

  const val = (obj, path) =>
    path.split(".").reduce((acc, k) => (acc ? acc[k] : undefined), obj);

  // Export helpers
  const exportXLSX = () => {
    const data = filteredRows.map((u, i) => {
      const obj = { "S.NO": i + 1 };
      visibleColumns.forEach((c) => {
        let v = val(u, c.field);
        if (c.field === "DOB" && v) v = String(v).split("T")[0];
        obj[c.headerName.toUpperCase()] = toStr(v);
      });
      return obj;
    });
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Users");
    XLSX.writeFile(wb, "UserList.xlsx");
  };
  const exportPDF = () => {
    if (filteredRows.length === 0) return swal("No data to export");
    const doc = new jsPDF("landscape", "mm", "a4", false);
    doc.setTextColor(5, 87, 97);
    doc.setFontSize(12);
    doc.text("User List", 14, 14);
    const head = [
      ["S.NO", ...visibleColumns.map((c) => c.headerName.toUpperCase())],
    ];
    const body = filteredRows.map((u, i) => [
      i + 1,
      ...visibleColumns.map((c) => {
        let v = val(u, c.field);
        if (c.field === "DOB" && v) v = String(v).split("T")[0];
        return toStr(v);
      }),
    ]);
    doc.autoTable({
      startY: 20,
      head,
      body,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [236, 240, 241], textColor: [15, 23, 42] },
      margin: { left: 10, right: 10 },
    });
    doc.save("UserList.pdf");
  };

  //
  // Column chooser operations
  //
  // availableSelectedRef.current is Set of field string chosen via checkboxes in Available list
  const onAvailableCheckbox = (checked, field) => {
    const s = new Set(availableSelectedRef.current);
    if (checked) s.add(field);
    else s.delete(field);
    availableSelectedRef.current = s;
  };

  // Move selected available => to selectedColumnDefs (HeadingRightShift)
  const moveAvailableToVisible = () => {
    const sel = Array.from(availableSelectedRef.current);
    if (!sel.length) return;
    // create objects {headerName, field} for each selected if not already in selectedColumnDefs
    const existingFields = new Set(selectedColumnDefs.map((c) => c.field));
    const newObjects = sel
      .filter((f) => !existingFields.has(f))
      .map((f) => {
        const base = BASE_COLS.find((b) => b.field === f);
        return base
          ? { headerName: base.headerName, field: base.field }
          : { headerName: f, field: f };
      });
    const updated = [...selectedColumnDefs, ...newObjects];
    setSelectedColumnDefs(updated);
    // clear available selection
    availableSelectedRef.current = new Set();
  };

  // Move selected visible (by visibleIndex) back to available list (handleLeftShift)
  const moveVisibleToAvailable = () => {
    if (visibleIndex < 0 || visibleIndex >= selectedColumnDefs.length) return;
    const copy = selectedColumnDefs.slice();
    copy.splice(visibleIndex, 1);
    setSelectedColumnDefs(copy);
    setVisibleIndex(-1);
  };

  // Remove icon immediate delete for visible item
  const removeVisibleAt = (index) => {
    const copy = selectedColumnDefs.slice();
    copy.splice(index, 1);
    setSelectedColumnDefs(copy);
    setVisibleIndex(-1);
  };

  // Up / Down reorder for visible list
  const moveVisibleUp = () => {
    if (visibleIndex <= 0) return;
    const copy = selectedColumnDefs.slice();
    const el = copy.splice(visibleIndex, 1)[0];
    copy.splice(visibleIndex - 1, 0, el);
    setSelectedColumnDefs(copy);
    setVisibleIndex(visibleIndex - 1);
  };
  const moveVisibleDown = () => {
    if (visibleIndex < 0 || visibleIndex >= selectedColumnDefs.length - 1)
      return;
    const copy = selectedColumnDefs.slice();
    const el = copy.splice(visibleIndex, 1)[0];
    copy.splice(visibleIndex + 1, 0, el);
    setSelectedColumnDefs(copy);
    setVisibleIndex(visibleIndex + 1);
  };

  // Apply: set AG Grid column defs and save to localStorage (AG Grid-like objects)
  const applyVisibleColumns = (e) => {
    e?.preventDefault?.();
    try {
      localStorage.setItem(
        STORAGE_COLS_KEY,
        JSON.stringify(selectedColumnDefs)
      );
    } catch {}
    setModal(false);
  };

  // Permissions reuse
  const permissions = CheckPermission
    ? CheckPermission("Create User") || {}
    : {};

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "20rem",
        }}
      >
        <Spinner style={{ height: "4rem", width: "4rem" }} color="primary">
          Loading...
        </Spinner>
      </div>
    );
  }

  return (
    <>
      {/* ===== Toolbar (inline, no-wrap) ===== */}
      <Row className="u-toolbar" style={{ marginBottom: 10 }}>
        <Col sm="12">
          <Card
            className="u-card"
            style={{ padding: 8, background: "#f4f7f6" }}
          >
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 10,
                alignItems: "center",
              }}
            >
              <div
                style={{
                  fontWeight: 800,
                  textTransform: "uppercase",
                  fontSize: 18,
                }}
              >
                User List{" "}
                <span className="count-pill">{filteredRows.length}</span>
              </div>

              <div style={{ flex: "1 1 240px" }} />

              <div className="toolbar-actions">
                {/* SuperAdmin UI (kept at end so it doesn't push buttons) */}
                {isMaster && (
                  <div style={{ marginLeft: 6 }}>
                    <SuperAdminUI
                      onDropdownChange={handleDropdownChange}
                      onSubmit={handleParentSubmit}
                    />
                  </div>
                )}
                <Input
                  placeholder="Search users..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ minWidth: 220 }}
                />

                <Button
                  style={{
                    backgroundColor: "rgb(8, 91, 245)",
                    color: "white",
                  }}
                  onClick={() =>
                    (window.location.href = "/app/master/superadmins/create/0")
                  }
                >
                  <FaPlus size={12} />{" "}
                  <span style={{ marginLeft: 6 }}>Add User</span>
                </Button>
                <Button
                  className="btn-icon-only"
                  color="primary"
                  outline
                  onClick={() => setModal(true)}
                  title="Columns"
                >
                  <FaFilter />
                </Button>

                {/* Exports */}
                <Button
                  outline
                  color="primary"
                  className="btn-outline-small"
                  onClick={exportXLSX}
                >
                  <ImDownload style={{ marginRight: 6 }} /> .XLSX
                </Button>
                <Button
                  outline
                  color="secondary"
                  className="btn-outline-small"
                  onClick={exportPDF}
                >
                  .PDF
                </Button>
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* duplicate note */}
      <Row>
        <Col sm="12">
          <div style={{ marginBottom: 8, color: "#64748b", fontSize: 12 }}>
            <b>Note:</b> Rows highlighted in{" "}
            <span className="dup-note">light blue</span> indicate same PAN or
            Aadhar exists in 2+ SuperAdmins.
          </div>
        </Col>
      </Row>

      {/* table */}
      <Row>
        <Col sm="12">
          <Card className="u-card" style={{ padding: 8 }}>
            <div className="u-table-wrap">
              <table className="u-table">
                <thead>
                  <tr>
                    <th>S.NO</th>
                    {visibleColumns.map((c) => (
                      <th key={c.field}>{c.headerName}</th>
                    ))}
                    <th>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={visibleColumns.length + 2}
                        style={{ padding: 18, color: "#6b7280" }}
                      >
                        No users found.
                      </td>
                    </tr>
                  ) : (
                    filteredRows.map((u, i) => {
                      const dup = isDuplicateRow(u);
                      return (
                        <tr
                          key={u?._id || i}
                          style={{
                            background: dup
                              ? "#e6f2ff"
                              : i % 2
                              ? "#f9fafb"
                              : "#fff",
                          }}
                        >
                          <td>{i + 1}</td>
                          {visibleColumns.map((c) => {
                            let value = val(u, c.field);
                            if (c.field === "DOB" && value) {
                              try {
                                value = String(value).split("T")[0];
                              } catch {}
                            }
                            const centerKeys = [
                              "id",
                              "pincode",
                              "Aadhar_No",
                              "Pan_No",
                            ];
                            return (
                              <td
                                key={c.field}
                                style={{
                                  textAlign: centerKeys.includes(c.field)
                                    ? "center"
                                    : "left",
                                }}
                                title={toStr(value)}
                              >
                                {c.field === "firstName" && u?._id ? (
                                  <Link
                                    className="chip-link"
                                    to={`/app/SoftNumen/account/CreateAccount/${u?._id}`}
                                  >
                                    {toStr(value)}
                                  </Link>
                                ) : (
                                  toStr(value)
                                )}
                              </td>
                            );
                          })}
                          <td>
                            <div className="actions">
                              <Link
                                title="View"
                                className="chip chip-view"
                                to={`/app/SoftNumen/account/ViewAccount/${u?._id}`}
                              >
                                <svg
                                  width="12"
                                  height="12"
                                  viewBox="0 0 24 24"
                                  fill="white"
                                >
                                  <path d="M12 5c-7 0-11 7-11 7s4 7 11 7 11-7 11-7-4-7-11-7zm0 12a5 5 0 110-10 5 5 0 010 10z" />
                                  <circle
                                    cx="12"
                                    cy="12"
                                    r="2.5"
                                    fill="white"
                                  />
                                </svg>
                              </Link>
                              <Link
                                title="Edit"
                                className="chip chip-edit"
                                to={`/app/SoftNumen/account/CreateAccount/${u?._id}`}
                              >
                                <svg
                                  width="12"
                                  height="12"
                                  viewBox="0 0 24 24"
                                  fill="white"
                                >
                                  <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1.003 1.003 0 000-1.42l-2.34-2.34a1.003 1.003 0 00-1.42 0l-1.83 1.83 3.75 3.75 1.84-1.82z" />
                                </svg>
                              </Link>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Column chooser modal — design matches your original file */}
      <Modal
        isOpen={modal}
        toggle={() => setModal(false)}
        className=""
        style={{ maxWidth: "1050px" }}
      >
        <ModalHeader toggle={() => setModal(false)}>Change Fields</ModalHeader>
        <ModalBody className="modalbodyhead">
          <Row>
            {/* Available Columns */}
            <Col lg="4" md="4" sm="12" xl="4" xs="12">
              <h4>Avilable Columns</h4>
              <div className="mainshffling">
                <div className="listbox">
                  {allColumnDefs.map((ele, i) => {
                    // if ele is an object with headerName/field shape, else transform
                    const field = ele.field || ele;
                    const header =
                      ele.headerName ||
                      ele.header ||
                      (BASE_COLS.find((b) => b.field === field) || {})
                        .headerName ||
                      field;
                    // checkbox should be disabled if already visible (to match original UX)
                    const alreadyVisible = selectedColumnDefs.some(
                      (s) => s.field === field
                    );
                    return (
                      <div key={String(field) + i} className="pick-item">
                        <label
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                          }}
                        >
                          <input
                            type="checkbox"
                            className="mx-1"
                            onChange={(e) =>
                              onAvailableCheckbox(e.target.checked, field)
                            }
                            disabled={alreadyVisible}
                          />
                          <span>{header}</span>
                        </label>
                      </div>
                    );
                  })}
                </div>
              </div>
            </Col>

            {/* Middle arrows */}
            <Col lg="2" md="2" sm="12" xl="2" xs="12" className="colarrowbtn">
              <div className="col-arrow-area">
                <div
                  onClick={moveAvailableToVisible}
                  className="arrow-btn"
                  title="Add selected →"
                >
                  <FaArrowAltCircleRight size={30} color="#1877f2" />
                </div>
                <div
                  onClick={moveVisibleToAvailable}
                  className="arrow-btn"
                  title="← Remove selected"
                >
                  <FaArrowAltCircleLeft size={30} color="#1877f2" />
                </div>
              </div>
            </Col>

            {/* Visible Columns */}
            <Col lg="6" md="6" sm="12" xl="6" xs="12">
              <Row>
                <Col lg="8" md="8" sm="12" xs="12">
                  <h4>Visible Columns</h4>
                  <div className="mainshffling">
                    <div className="listbox">
                      {selectedColumnDefs.map((ele, i) => (
                        <div
                          key={ele.field + i}
                          className={`pick-item ${
                            visibleIndex === i ? "active" : ""
                          }`}
                          onClick={() => setVisibleIndex(i)}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                            }}
                          >
                            <IoMdRemoveCircleOutline
                              onClick={(ev) => {
                                ev.stopPropagation();
                                removeVisibleAt(i);
                              }}
                              style={{ cursor: "pointer" }}
                              size="22"
                              color="red"
                              className="mr-1"
                            />
                            <div>{ele.headerName}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </Col>

                <Col lg="4" md="4" sm="12" xs="12">
                  <div
                    className="updownbtn justify-content-center"
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 10,
                      alignItems: "center",
                      marginTop: 16,
                    }}
                  >
                    <div>
                      <BsFillArrowUpSquareFill
                        className="arrowassign mb-1"
                        size="30px"
                        onClick={moveVisibleUp}
                      />
                    </div>
                    <div>
                      <BsFillArrowDownSquareFill
                        onClick={moveVisibleDown}
                        className="arrowassign"
                        size="30px"
                      />
                    </div>
                  </div>
                </Col>
              </Row>
            </Col>
          </Row>

          <Row style={{ marginTop: 12 }}>
            <Col>
              <div className="d-flex justify-content-center">
                <Button onClick={applyVisibleColumns} color="primary">
                  Submit
                </Button>
              </div>
            </Col>
          </Row>
        </ModalBody>
      </Modal>
    </>
  );
}

// helper to resolve dotted paths
function val(obj, path) {
  return path.split(".").reduce((acc, k) => (acc ? acc[k] : undefined), obj);
}
